/**
 * Azure Config Client
 * Busca configuração Azure (URL e Key) do Lovable Cloud via Edge Function
 */

import axios from "axios";
import { logger } from "@/utils/logger";

export interface AzureConfig {
  azureFunctionsUrl: string;
  azureFunctionsKey: string;
}

interface ConfigResponse {
  success: boolean;
  data?: AzureConfig;
  error?: string;
}

// Cache in-memory
let configCache: AzureConfig | null = null;

/**
 * Busca configuração Azure do Lovable Cloud
 */
export const getAzureConfig = async (): Promise<AzureConfig> => {
  // Retornar cache se disponível
  if (configCache) {
    logger.log("[AzureConfigClient] Using cached config");
    return configCache;
  }

  try {
    logger.log(
      "[AzureConfigClient] Fetching Azure config from Lovable Cloud..."
    );

    // Debug: verificar variáveis disponíveis
    logger.log("[AzureConfigClient] Environment check:", {
      hasSupabaseUrl: !!import.meta.env.VITE_SUPABASE_URL,
      hasSupabaseKey: !!import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
    });

    // Buscar URL do Supabase do .env com fallback
    const supabaseUrl =
      import.meta.env.VITE_SUPABASE_URL ||
      "https://wgoudtgwhmmtroajtrls.supabase.co";
    const supabaseAnonKey =
      import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Indnb3VkdGd3aG1tdHJvYWp0cmxzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjAzODYxNjksImV4cCI6MjA3NTk2MjE2OX0.RR2CstcmnBOc89Ahs7uTKieE7BEHReD3mjdokEKftW0";

    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error("Supabase configuration not available");
    }

    logger.log(
      "[AzureConfigClient] Calling edge function at:",
      `${supabaseUrl}/functions/v1/get-azure-config`
    );

    // Chamar edge function com axios
    const response = await axios.get<ConfigResponse>(
      `${supabaseUrl}/functions/v1/get-azure-config`,
      {
        headers: {
          Authorization: `Bearer ${supabaseAnonKey}`,
          "Content-Type": "application/json",
        },
        timeout: 30000,
      }
    );

    logger.log("[AzureConfigClient] Edge function response:", {
      status: response.status,
      success: response.data?.success,
    });

    const responseData = response.data;

    if (!responseData?.success || !responseData.data) {
      throw new Error(
        responseData?.error || "Failed to retrieve Azure configuration"
      );
    }

    // Validar configuração
    const { azureFunctionsUrl, azureFunctionsKey } = responseData.data;

    if (!azureFunctionsUrl || !azureFunctionsKey) {
      throw new Error("Invalid Azure configuration received");
    }

    // Armazenar em cache
    configCache = responseData.data;

    logger.log("[AzureConfigClient] ✅ Azure config retrieved successfully:", {
      azureFunctionsUrl: configCache.azureFunctionsUrl,
      hasKey: !!configCache.azureFunctionsKey,
    });

    return configCache;
  } catch (error: any) {
    logger.error("[AzureConfigClient] Error fetching config:", error);
    logger.error("[AzureConfigClient] Error details:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

/**
 * Limpa cache de configuração
 */
export const clearAzureConfigCache = (): void => {
  configCache = null;
  logger.log("[AzureConfigClient] Cache cleared");
};
